import * as React from 'react';
import {useNativeNode} from '../useNativeNode';
import {reportBug} from '@packages/utilities/reporter';
import {panelOpened} from '@packages/systems/panels';
import {useDangerousHooks, TypeImplementation} from '@packages/systems/wfdl';
import {
  Lottie,
  useLottieIndexDB,
} from '@packages/systems/ui/shared-ui/Lottie/Lottie';
import {ASSET_MANAGER_HOT_SELECT_STARTED} from '@packages/systems/designer/actions';
import {
  ContextualExpressions,
  stripAnn,
} from '@packages/utilities/contextual-expression';
import {
  useDesignerSelector,
  useDispatch,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import type {LottieModule} from '../modules/LottieSiteModule';
import {
  RefreshIcon,
  Button,
  AssetManagerIcon,
  VStack,
  Text,
  Divider,
  HStack,
  View,
  GuideIcon,
  Note,
  NoteBody,
} from '@packages/systems/spring';
import {emotionStyled} from '@packages/utilities/styled';
import {useExpressionEditorImplementationContext} from '@packages/systems/ExpressionEditor-utils';

// should be imported from package:
// import {ASSETS_PANEL} from '@packages/systems/designer/assets';
const ASSETS_PANEL = 'assets';

const ANIMATION_VAR = '--local-animation-width';

const AnimationBar = emotionStyled('div')(
  {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: `var(${ANIMATION_VAR})%`,
    background: '$background1',
    borderRadius: 0,
  },
  {
    variants: {
      isDone: {
        true: {
          borderRadius: '$2',
        },
      },
    },
  }
);

const ProgressBar = ({
  label,
  progress,
  onClick,
}: {
  label: string;
  progress: number;
  onClick: () => void;
}) => {
  return (
    <View
      onClick={onClick}
      height={26}
      borderRadius="$2"
      borderWidth="$0.25"
      borderStyle="solid"
      borderColor="$border2"
      background="$background2"
      position="relative"
      overflow="hidden"
    >
      <AnimationBar
        style={{[ANIMATION_VAR]: progress} as React.CSSProperties}
        isDone={progress === 100}
      />
      <View
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Text>{label}</Text>
      </View>
    </View>
  );
};

const hotSelectingSelector = (state: DesignerStores) =>
  state.AssetManagerUIStore.hotSelecting;

type InstanceRef = ReturnType<LottieModule['createInstance']>;

/**
 * Creates the instance and returns its total duration as well as total frames
 * whenever config or selected node changes
 */
const useLottieInstance = (src: string | null) => {
  const {getNativeNode, getActiveWindow} = useDangerousHooks();
  const nativeNode = useNativeNode();
  const instanceRef = React.useRef<InstanceRef | undefined>(undefined);
  const [progress, setProgress] = React.useState(0);

  const resetPreview = React.useCallback(() => {
    setProgress(0);
  }, []);

  const handlePlaybackComplete = React.useCallback(() => {
    setProgress(0);
    const instance = instanceRef.current;

    if (!instance) {
      return;
    }

    const initialIx2Value = instance.ix2InitialValue;
    if (initialIx2Value != null) {
      instance.goToFrame((instance.frames * initialIx2Value) / 100);
    } else {
      instance.goToFrame(instance.direction === 1 ? 0 : instance.frames);
    }
  }, []);

  React.useEffect(() => {
    const lottie = getActiveWindow().window.Webflow.require('lottie');

    if (!lottie || !nativeNode || !src) {
      return;
    }

    const instance = lottie.createInstance(nativeNode);

    if (instance) {
      instance.onFrameChange(setProgress);
      instance.onPlaybackComplete(handlePlaybackComplete);
      instance.onDestroy(resetPreview);
      instanceRef.current = instance;
    } else {
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug(
        'LottieInstanceNotFound',
        'Instance could not be found in the Designer settings menu.'
      );
    }

    return () => {
      resetPreview();
    };
  }, [src, nativeNode, getActiveWindow, resetPreview, handlePlaybackComplete]);

  React.useEffect(() => {
    return () => {
      if (instanceRef.current && instanceRef.current.isPlaying) {
        instanceRef.current.stop();
        handlePlaybackComplete();
      }
    };
  }, []);

  const onPreview = () => {
    if (getNativeNode() === nativeNode && nativeNode && instanceRef.current) {
      instanceRef.current.play();
    }
  };

  const onStop = () => {
    if (instanceRef.current) {
      instanceRef.current.stop();
      handlePlaybackComplete();
    }
  };

  return {
    onPreview,
    onStop,
    progress,
    hasNativeNode: nativeNode != null,
  };
};

// @ts-expect-error - TS2525 - Initializer provides no value for this binding element and the binding element has no default value. | TS2525 - Initializer provides no value for this binding element and the binding element has no default value. | TS7031 - Binding element 'hooks' implicitly has an 'any' type.
const LottieSettings = ({state: {src, origFileName} = {}, asset}) => {
  const [hover, setHover] = React.useState(false);

  const {onPreview, onStop, progress, hasNativeNode} = useLottieInstance(src);

  const animationData = useLottieIndexDB(src, false);

  const {op = null, fr: frameRate = null} = animationData || {};
  const totalFrames = op ? Math.floor(op) : null;
  const totalDuration =
    frameRate && totalFrames ? totalFrames / frameRate : null;

  return (
    <VStack>
      <HStack align="start">
        <View
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          height={64}
          width={64}
          borderRadius="$2"
          flexShrink={0}
          borderStyle="solid"
          overflow="hidden"
          borderWidth="$0.25"
          borderColor="$border1"
        >
          <Lottie
            data-location="lottie-settings"
            path={src}
            hover={hover}
            isUploading={false}
          />
        </View>
        <VStack spacing={0} minWidth={0}>
          <HStack spacing={1} align="center">
            {asset?.origin?.length ? (
              <GuideIcon colorVariant="secondary" />
            ) : null}
            <Text truncateToLines={1}>{origFileName}</Text>
          </HStack>

          {totalFrames == null ? null : (
            <Text colorVariant="dimmed">{totalFrames} frames</Text>
          )}
          {totalDuration == null ? null : (
            <Text colorVariant="dimmed">
              {totalDuration.toFixed(2).replace(/\.00$/, '')} seconds
            </Text>
          )}
        </VStack>
      </HStack>
      <Divider />
      {hasNativeNode && progress > 0 ? (
        <ProgressBar
          progress={progress}
          label={`${progress}%`}
          onClick={onStop}
        />
      ) : (
        <Button isFullWidth onClick={onPreview} isDisabled={!hasNativeNode}>
          Preview Animation
        </Button>
      )}
      {!hasNativeNode && (
        <Note>
          <NoteBody>
            Show this element on the canvas to preview it or edit its duration.
          </NoteBody>
        </Note>
      )}
    </VStack>
  );
};

type State = null | {
  /**
   * @deprecated Give preference to recover the asset URL using `id` instead.
   */
  src: string;
  origFileName: string;
  id: string;
};

export const path = TypeImplementation.Custom({
  render: function AnimationSettings({expr}) {
    const hotSelecting = useDesignerSelector(hotSelectingSelector);
    const fluxDispatch = useDispatch();
    const {env} = useExpressionEditorImplementationContext();

    const literalVal = ContextualExpressions.getLiteral(stripAnn(expr));
    if (literalVal === null) {
      return null;
    }
    const literal = literalVal.value;
    // @ts-expect-error - TS2322 - Type 'Json' is not assignable to type 'State'.
    const state: State = literal;
    const asset =
      state && state.id && env.asset.get({type: 'all', id: state.id});

    const isPathSet = state && state.src;
    const label = isPathSet ? 'Replace' : 'Choose';

    return (
      <VStack>
        <Button
          isFullWidth
          onClick={() => {
            // flux dispatch to startHotSelect
            fluxDispatch({
              type: ASSET_MANAGER_HOT_SELECT_STARTED,
              payload: {mode: 'animationSettings'},
            });
            fluxDispatch(panelOpened(ASSETS_PANEL));
          }}
          prefix={isPathSet ? <RefreshIcon /> : <AssetManagerIcon />}
          isDisabled={hotSelecting}
          density="compact"
        >
          {label}&nbsp;Lottie Sequence
        </Button>
        {isPathSet ? <LottieSettings state={state} asset={asset} /> : null}
      </VStack>
    );
  },
  system: {
    labelVariant: 'hidden',
    defaultValue: null,
  },
});
